import { useEffect, useState, useCallback } from "react";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, CheckCircle2, AlertTriangle, Megaphone, Image as ImageIcon } from "lucide-react";

export const PROPERTY_TYPES = [
  "Detached house", "Semi-detached house", "Terraced house", "End terrace house", "Town house",
  "Cottage", "Bungalow", "Detached bungalow", "Semi-detached bungalow", "Flat", "Studio",
  "Maisonette", "Apartment", "Penthouse", "Mews", "Link-detached house", "Land", "Farm",
  "Barn conversion", "Park home", "Block of apartments",
];
const BANDS = ["A", "B", "C", "D", "E", "F", "G", "H"];
const TENURES = ["Freehold", "Leasehold", "Share of Freehold", "Commonhold"];
const QUALIFIERS = ["Default", "Guide Price", "Offers in Region of", "Offers in Excess of", "Fixed Price", "From", "Shared Ownership", "POA"];
const FURNISHED = ["Furnished", "Unfurnished", "Part Furnished"];

const idKey = (l, kind) => (kind === "sale" ? l.sale_id : l.letting_id);

// Batch-load portal readiness for a list of listings.
export function usePortalReadiness(kind, items) {
  const [map, setMap] = useState({});
  const refresh = useCallback(async () => {
    if (!items || items.length === 0) { setMap({}); return; }
    const base = kind === "sale" ? "sales" : "lettings";
    const results = await Promise.all(items.map((l) =>
      api.get(`/${base}/${idKey(l, kind)}/portal-preview`).then(({ data }) => [idKey(l, kind), data]).catch(() => [idKey(l, kind), null])
    ));
    setMap(Object.fromEntries(results.filter(([, v]) => v)));
  }, [kind, items]);
  useEffect(() => { refresh(); }, [refresh]);
  return { map, refresh };
}

export const PortalBadge = ({ readiness, testid }) => {
  if (!readiness) return null;
  if (readiness.ready) {
    return (
      <span data-testid={testid} className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
        <CheckCircle2 size={11} /> Portal ready
      </span>
    );
  }
  const n = readiness.missing?.length || 0;
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" title={(readiness.missing || []).join(", ")}>
      <AlertTriangle size={11} /> {n} detail{n === 1 ? "" : "s"} needed
    </span>
  );
};

const Field = ({ label, children }) => (
  <div><label className="block text-[11px] font-medium text-slate-500 mb-1">{label}</label>{children}</div>
);
const inp = "w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600";

export function MarketingModal({ kind, listing, onClose, onSaved }) {
  const base = kind === "sale" ? "sales" : "lettings";
  const [prop, setProp] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pForm, setPForm] = useState({});   // property-level
  const [lForm, setLForm] = useState({});   // listing-level

  useEffect(() => {
    (async () => {
      let p = null;
      if (listing.property_id) {
        try { p = (await api.get("/properties")).data.find((x) => x.property_id === listing.property_id); } catch { /* noop */ }
      }
      setProp(p);
      setPForm({
        property_type: p?.property_type || "", tenure: p?.tenure || "", council_tax_band: p?.council_tax_band || "",
        receptions: p?.receptions ?? 1, epc_eer_current: p?.epc_eer_current ?? "", epc_exempt: p?.epc_exempt || false,
        county: p?.county || "", display_address: p?.display_address || "",
        latitude: p?.latitude ?? "", longitude: p?.longitude ?? "",
      });
      setLForm({
        summary: listing.summary || "", description: listing.description || "",
        features: (listing.features || []).join("\n"),
        virtual_tour_url: listing.virtual_tour_url || "",
        image_file_ids: listing.image_file_ids || [],
        epc_file_id: listing.epc_file_id || "",
        price_qualifier: listing.price_qualifier || "Default",
        new_home: listing.new_home || false, shared_ownership: listing.shared_ownership || false,
        retirement: listing.retirement || false, auction: listing.auction || false,
        furnished_state: listing.furnished_state || "", rent_frequency: listing.rent_frequency || "pcm",
        deposit: listing.deposit ?? "", available_from: listing.available_from || "",
        min_tenancy_months: listing.min_tenancy_months ?? "",
        bills_included: listing.bills_included || false, pets_allowed: listing.pets_allowed || false, students_allowed: listing.students_allowed || false,
      });
      if (p) {
        try { setPhotos((await api.get(`/files?entity_type=property&entity_id=${p.property_id}&bucket=photos`)).data); } catch { /* noop */ }
      }
      loadReadiness();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadReadiness = () => api.get(`/${base}/${idKey(listing, kind)}/portal-preview`).then(({ data }) => setReadiness(data)).catch(() => {});

  const toggleImg = (fid) => setLForm((f) => ({ ...f,
    image_file_ids: f.image_file_ids.includes(fid) ? f.image_file_ids.filter((x) => x !== fid) : [...f.image_file_ids, fid] }));

  const num = (v) => (v === "" || v === null ? undefined : Number(v));

  const save = async () => {
    setBusy(true);
    try {
      if (prop) {
        await api.patch(`/properties/${prop.property_id}/marketing`, {
          property_type: pForm.property_type || undefined, tenure: pForm.tenure || undefined,
          council_tax_band: pForm.council_tax_band || undefined, receptions: num(pForm.receptions),
          epc_eer_current: num(pForm.epc_eer_current), epc_exempt: pForm.epc_exempt,
          county: pForm.county || undefined, display_address: pForm.display_address || undefined,
          latitude: num(pForm.latitude), longitude: num(pForm.longitude),
        });
      }
      const lPayload = {
        summary: lForm.summary, description: lForm.description,
        features: lForm.features.split("\n").map((s) => s.trim()).filter(Boolean),
        virtual_tour_url: lForm.virtual_tour_url, image_file_ids: lForm.image_file_ids, epc_file_id: lForm.epc_file_id,
      };
      if (kind === "sale") {
        Object.assign(lPayload, { price_qualifier: lForm.price_qualifier, new_home: lForm.new_home,
          shared_ownership: lForm.shared_ownership, retirement: lForm.retirement, auction: lForm.auction });
      } else {
        Object.assign(lPayload, { furnished_state: lForm.furnished_state, rent_frequency: lForm.rent_frequency,
          deposit: num(lForm.deposit) ?? 0, available_from: lForm.available_from || null,
          min_tenancy_months: num(lForm.min_tenancy_months) ?? null,
          bills_included: lForm.bills_included, pets_allowed: lForm.pets_allowed, students_allowed: lForm.students_allowed });
      }
      await api.patch(`/${base}/${idKey(listing, kind)}`, lPayload);
      toast.success("Portal details saved");
      onSaved && onSaved();
      loadReadiness();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div data-testid="marketing-modal" className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><Megaphone size={18} className="text-blue-600" /> Portal listing details</h3>
          <button data-testid="close-marketing" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>

        {/* Readiness panel */}
        {readiness && (
          <div data-testid="readiness-panel" className={`rounded-lg border p-3 mb-4 ${readiness.ready ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
            {readiness.ready ? (
              <p className="text-sm font-semibold text-emerald-700 flex items-center gap-1.5"><CheckCircle2 size={15} /> This listing has everything Zoopla needs.</p>
            ) : (
              <>
                <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5 mb-1"><AlertTriangle size={15} /> Still needed for a Zoopla-ready listing:</p>
                <ul className="text-xs text-amber-700 list-disc list-inside">
                  {readiness.missing.map((m) => <li key={m}>{m}</li>)}
                </ul>
              </>
            )}
          </div>
        )}

        {!prop && listing.property_id && <p className="text-xs text-amber-600 mb-3">Loading linked property…</p>}
        {!listing.property_id && <p className="text-xs text-amber-600 mb-3">This listing isn't linked to a saved property, so property attributes (type, EPC, location) can't be set. Recreate it from a saved property to enable them.</p>}

        {/* Property-level attributes */}
        {prop && (
          <div className="mb-4">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Property attributes</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Property type"><select data-testid="mk-property-type" className={inp} value={pForm.property_type} onChange={(e) => setPForm({ ...pForm, property_type: e.target.value })}><option value="">— Select —</option>{PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></Field>
              <Field label="Receptions"><input data-testid="mk-receptions" type="number" min="0" className={inp} value={pForm.receptions} onChange={(e) => setPForm({ ...pForm, receptions: e.target.value })} /></Field>
              {kind === "sale" && <Field label="Tenure"><select data-testid="mk-tenure" className={inp} value={pForm.tenure} onChange={(e) => setPForm({ ...pForm, tenure: e.target.value })}><option value="">— Select —</option>{TENURES.map((t) => <option key={t} value={t}>{t}</option>)}</select></Field>}
              <Field label="Council tax band"><select data-testid="mk-band" className={inp} value={pForm.council_tax_band} onChange={(e) => setPForm({ ...pForm, council_tax_band: e.target.value })}><option value="">— Select —</option>{BANDS.map((b) => <option key={b} value={b}>{b}</option>)}</select></Field>
              <Field label="EPC energy rating (1-100)"><input data-testid="mk-epc" type="number" min="1" max="100" className={inp} value={pForm.epc_eer_current} onChange={(e) => setPForm({ ...pForm, epc_eer_current: e.target.value })} disabled={pForm.epc_exempt} /></Field>
              <Field label="County"><input data-testid="mk-county" className={inp} value={pForm.county} onChange={(e) => setPForm({ ...pForm, county: e.target.value })} /></Field>
              <Field label="Latitude"><input data-testid="mk-lat" type="number" step="any" className={inp} value={pForm.latitude} onChange={(e) => setPForm({ ...pForm, latitude: e.target.value })} placeholder="53.4808" /></Field>
              <Field label="Longitude"><input data-testid="mk-lng" type="number" step="any" className={inp} value={pForm.longitude} onChange={(e) => setPForm({ ...pForm, longitude: e.target.value })} placeholder="-2.2426" /></Field>
              <div className="col-span-2"><Field label="Display address (public)"><input data-testid="mk-display-address" className={inp} value={pForm.display_address} onChange={(e) => setPForm({ ...pForm, display_address: e.target.value })} placeholder="e.g. Deansgate, Manchester M3" /></Field></div>
              <label className="col-span-2 flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" data-testid="mk-epc-exempt" checked={pForm.epc_exempt} onChange={(e) => setPForm({ ...pForm, epc_exempt: e.target.checked })} /> EPC exempt (no rating required)</label>
            </div>
          </div>
        )}

        {/* Listing marketing content */}
        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Advert content</p>
        <div className="space-y-3">
          <Field label="Summary (one line)"><input data-testid="mk-summary" className={inp} value={lForm.summary} onChange={(e) => setLForm({ ...lForm, summary: e.target.value })} /></Field>
          <Field label="Full description"><textarea data-testid="mk-description" rows={4} className={inp} value={lForm.description} onChange={(e) => setLForm({ ...lForm, description: e.target.value })} /></Field>
          <Field label="Key features (one per line)"><textarea data-testid="mk-features" rows={3} className={inp} value={lForm.features} onChange={(e) => setLForm({ ...lForm, features: e.target.value })} placeholder={"Modern kitchen\nEnclosed garden\nOff-street parking"} /></Field>
          <Field label="Virtual tour URL"><input data-testid="mk-tour" className={inp} value={lForm.virtual_tour_url} onChange={(e) => setLForm({ ...lForm, virtual_tour_url: e.target.value })} placeholder="https://…" /></Field>

          {kind === "sale" ? (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Price qualifier"><select data-testid="mk-qualifier" className={inp} value={lForm.price_qualifier} onChange={(e) => setLForm({ ...lForm, price_qualifier: e.target.value })}>{QUALIFIERS.map((q) => <option key={q} value={q}>{q}</option>)}</select></Field>
              <div className="flex flex-col justify-end gap-1 text-xs text-slate-600">
                <label className="flex items-center gap-1.5"><input type="checkbox" checked={lForm.new_home} onChange={(e) => setLForm({ ...lForm, new_home: e.target.checked })} /> New home</label>
                <label className="flex items-center gap-1.5"><input type="checkbox" checked={lForm.shared_ownership} onChange={(e) => setLForm({ ...lForm, shared_ownership: e.target.checked })} /> Shared ownership</label>
                <label className="flex items-center gap-1.5"><input type="checkbox" checked={lForm.retirement} onChange={(e) => setLForm({ ...lForm, retirement: e.target.checked })} /> Retirement</label>
                <label className="flex items-center gap-1.5"><input type="checkbox" checked={lForm.auction} onChange={(e) => setLForm({ ...lForm, auction: e.target.checked })} /> Auction</label>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Furnished state"><select data-testid="mk-furnished" className={inp} value={lForm.furnished_state} onChange={(e) => setLForm({ ...lForm, furnished_state: e.target.value })}><option value="">— Select —</option>{FURNISHED.map((f) => <option key={f} value={f}>{f}</option>)}</select></Field>
              <Field label="Deposit (£)"><input data-testid="mk-deposit" type="number" min="0" className={inp} value={lForm.deposit} onChange={(e) => setLForm({ ...lForm, deposit: e.target.value })} /></Field>
              <Field label="Available from"><input data-testid="mk-available" type="date" className={inp} value={lForm.available_from ? lForm.available_from.slice(0, 10) : ""} onChange={(e) => setLForm({ ...lForm, available_from: e.target.value })} /></Field>
              <Field label="Min tenancy (months)"><input data-testid="mk-min-tenancy" type="number" min="0" className={inp} value={lForm.min_tenancy_months} onChange={(e) => setLForm({ ...lForm, min_tenancy_months: e.target.value })} /></Field>
              <div className="col-span-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                <label className="flex items-center gap-1.5"><input type="checkbox" checked={lForm.bills_included} onChange={(e) => setLForm({ ...lForm, bills_included: e.target.checked })} /> Bills included</label>
                <label className="flex items-center gap-1.5"><input type="checkbox" checked={lForm.pets_allowed} onChange={(e) => setLForm({ ...lForm, pets_allowed: e.target.checked })} /> Pets allowed</label>
                <label className="flex items-center gap-1.5"><input type="checkbox" checked={lForm.students_allowed} onChange={(e) => setLForm({ ...lForm, students_allowed: e.target.checked })} /> Students welcome</label>
              </div>
            </div>
          )}

          {/* Photo picker */}
          <Field label={`Advert photos (${lForm.image_file_ids?.length || 0} selected)`}>
            {photos.length === 0 ? (
              <p className="text-xs text-slate-400 flex items-center gap-1.5"><ImageIcon size={13} /> No property photos yet — add photos on the property page, then select them here.</p>
            ) : (
              <div className="grid grid-cols-4 gap-2" data-testid="mk-photo-picker">
                {photos.map((ph) => {
                  const on = lForm.image_file_ids.includes(ph.file_id);
                  return (
                    <button type="button" key={ph.file_id} data-testid={`mk-photo-${ph.file_id}`} onClick={() => toggleImg(ph.file_id)}
                      className={`relative rounded-lg overflow-hidden border-2 aspect-square ${on ? "border-blue-600" : "border-transparent"}`}>
                      <img src={fileUrl(ph.file_id)} alt="" className="w-full h-full object-cover" />
                      {on && <span className="absolute top-1 right-1 bg-blue-600 text-white rounded-full w-4 h-4 flex items-center justify-center"><CheckCircle2 size={12} /></span>}
                    </button>
                  );
                })}
              </div>
            )}
          </Field>
        </div>

        <div className="flex items-center gap-2 mt-5">
          <button data-testid="save-marketing" onClick={save} disabled={busy} className="bg-blue-700 hover:bg-blue-800 text-white rounded-lg px-4 py-2 text-sm font-semibold flex items-center gap-1.5 disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Save details
          </button>
          <button onClick={onClose} className="border border-slate-300 text-slate-600 hover:bg-slate-50 rounded-lg px-4 py-2 text-sm font-medium">Close</button>
        </div>
      </div>
    </div>
  );
}
